import { DefaultError, UseMutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { mapUpdateRequest } from '#/components/Pages/Bookings/EditReservationTripDetails/lib/mapUpdateRequest';
import { useAppStateStore } from '#/hooks/useAppStateStore';
import { useAuthenticatedSelfServiceApi } from '#/hooks/useAuthenticatedSelfServiceApi';
import { reservationDataQueryKeys } from '#/services/queryKeys';
import { RentalSelfServiceApiClient } from '#/services/rentalSelfServiceApiClient';
import { mapUpdateBookingValuesToRequest } from '@jucy-ui/common';
import { CreateBookingResponse } from '@jucy/rentals-api-client/rentals-api-v3/models/CreateBookingResponse';
import { ValidateUpdateResponse } from '@jucy/rentals-api-client/rentals-api-v3/models/ValidateUpdateResponse';

type UseUpdateReservationOptions = Omit<
    UseMutationOptions<CreateBookingResponse, DefaultError, ValidateUpdateResponse>,
    'mutationFn' | 'mutationKey'
>;

export const updateReservationKeys = {
    all: ['update-reservation'],
    forRef: (reservationReference?: string) => [...updateReservationKeys.all, 'ref', reservationReference] as const,
};

export const useUpdateReservation = (reservationRef: string, options?: UseUpdateReservationOptions) => {
    const queryClient = useQueryClient();
    const appStore = useAppStateStore();
    const rentalApiClient = useAuthenticatedSelfServiceApi() as RentalSelfServiceApiClient;

    return useMutation({
        ...options,
        mutationKey: updateReservationKeys.forRef(reservationRef),
        mutationFn: (amendment: ValidateUpdateResponse) =>
            rentalApiClient.v3.updateSelfServiceReservation(
                reservationRef,
                mapUpdateBookingValuesToRequest(mapUpdateRequest(amendment)).updateReservationRequest
            ),
        onSuccess: async (update, variables, context) => {
            queryClient.setQueryData(reservationDataQueryKeys.booking(reservationRef), update.booking);
            await queryClient.invalidateQueries({
                predicate: (query) => query.queryKey.includes(reservationRef),
            });
            await options?.onSuccess?.(update, variables, context);
            if (appStore.selectedReservation?.reservationReference === reservationRef) {
                await appStore.refreshSelectedReservation({ force: true });
            }
        },
    });
};
